/**
 * Button primitive — Cohere 2026 design system.
 *
 * Five variants (primary, secondary, pillOutline, ghost, danger) styled
 * exclusively via CSS custom-property references. Focus ring uses
 * :focus-visible (not :focus) for keyboard-only display.
 *
 * @see specs/integration-plan.md Section 3.1
 */
import React from "react";
import type { ButtonHTMLAttributes, CSSProperties } from "react";
import { Icon, type IconName } from "./Icon";

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "pillOutline" | "ghost" | "danger";
  icon?: IconName;
  iconRight?: IconName;
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Variant style maps — all values are CSS custom-property references */
/* ------------------------------------------------------------------ */

const VARIANT_STYLES: Record<
  NonNullable<ButtonProps["variant"]>,
  CSSProperties
> = {
  primary: {
    background: "var(--color-primary)",
    color: "var(--color-on-primary)",
    border: "none",
    borderRadius: "var(--radius-pill)",
    padding: "var(--space-md) var(--space-xl)",
  },
  secondary: {
    backgroundColor: "transparent",
    color: "var(--fg1)",
    border: "none",
    borderRadius: "var(--radius-xs)",
    padding: "var(--space-sm) 0",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
  },
  pillOutline: {
    backgroundColor: "transparent",
    color: "var(--color-primary)",
    border: "1px solid var(--color-primary)",
    borderRadius: "var(--radius-xl)",
    padding: "var(--space-xs) var(--space-md)",
  },
  ghost: {
    backgroundColor: "transparent",
    color: "var(--fg1)",
    borderStyle: "none",
    borderWidth: 0,
    borderRadius: "var(--radius-xl)",
    padding: "var(--space-xs) var(--space-md)",
  },
  danger: {
    backgroundColor: "var(--color-error)",
    color: "#ffffff",
    border: "none",
    borderRadius: "var(--radius-pill)",
    padding: "var(--space-md) var(--space-xl)",
  },
};

/* ------------------------------------------------------------------ */
/*  Size style maps                                                    */
/* ------------------------------------------------------------------ */

const SIZE_STYLES: Record<NonNullable<ButtonProps["size"]>, CSSProperties> = {
  sm: { fontSize: "var(--fs-micro)", padding: "var(--space-xs) var(--space-md)" },
  md: { fontSize: "var(--fs-button)" },
  lg: { fontSize: "var(--fs-body-lg)", padding: "var(--space-lg) var(--space-2xl)" },
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function Button({
  variant = "primary",
  size = "md",
  icon,
  iconRight,
  children,
  className,
  style: userStyle,
  disabled,
  ...rest
}: ButtonProps) {
  const variantStyle = VARIANT_STYLES[variant];
  const sizeStyle = SIZE_STYLES[size];

  const composedStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--space-xs)",
    fontFamily: "var(--font-ui)",
    fontWeight: 500,
    lineHeight: 1.4,
    cursor: "pointer",
    transition: "opacity 0.15s ease, background 0.15s ease",
    outline: "none",
    ...variantStyle,
    ...sizeStyle,
    ...(disabled
      ? { opacity: 0.5, pointerEvents: "none" as const }
      : undefined),
    ...userStyle,
  };

  const variantClass = `btn-${variant}`;
  const sizeClass = `btn-${size}`;
  const classes = [variantClass, sizeClass, className].filter(Boolean).join(" ");

  return (
    <button
      type="button"
      className={classes}
      data-variant={variant}
      data-size={size}
      style={composedStyle}
      disabled={disabled}
      {...rest}
    >
      {icon && <Icon name={icon} size={16} />}
      {children}
      {iconRight && <Icon name={iconRight} size={16} />}
    </button>
  );
}

export default Button;
